import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Search, MapPin, Phone, MessageCircle, ChevronRight, Wrench,
  Clock, Shield, Truck, Star, CheckCircle2, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// SEO Landing Page Component - For city-specific pages like /reparacion-madrid
const SEOLandingPage = () => {
  const { citySlug } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  
  const [cityData, setCityData] = useState(null);
  const [brands, setBrands] = useState([]);
  const [popularRepairs, setPopularRepairs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCityData();
    fetchBrands();
  }, [citySlug]);

  const fetchCityData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/seo/cities/${citySlug}`);
      if (res.ok) {
        const data = await res.json();
        setCityData(data.city);
        setPopularRepairs(data.popular_repairs || []);
      }
    } catch (error) {
      console.error('Error fetching city data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await fetch(`${API_URL}/api/seo/brands`);
      if (res.ok) {
        const data = await res.json();
        setBrands(data);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.length < 2) return;
    
    try {
      const res = await fetch(`${API_URL}/api/seo/search?q=${encodeURIComponent(searchQuery)}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0047FF]"></div>
      </div>
    );
  }

  const cityName = cityData?.name || citySlug?.replace('-', ' ');

  return (
    <>
      <Helmet>
        <title>{cityData?.meta_title || `Reparación de Electrónica en ${cityName} | Arreglatelo`}</title>
        <meta name="description" content={cityData?.meta_description || `Servicio técnico en ${cityName}. Reparamos consolas, móviles, robots aspiradores con envío gratis.`} />
        <link rel="canonical" href={`https://rma.arreglatelo.es/reparacion-${citySlug}`} />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": `Arreglatelo - Servicio Técnico ${cityName}`,
            "telephone": "+34624473293",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": cityName,
              "addressCountry": "ES"
            },
            "areaServed": { "@type": "City", "name": cityName }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white" data-testid="seo-city-page">
        {/* Header */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0047FF] rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <span className="font-bold text-xl">Arreglatelo</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <a href="tel:+34624473293" className="hidden md:flex items-center gap-2 text-sm text-gray-600 hover:text-[#0047FF]">
                <Phone className="w-4 h-4" /> 624 47 32 93
              </a>
              <Link to="/register">
                <Button className="bg-[#0047FF] hover:bg-[#003BCC]">
                  Solicitar Reparación
                </Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-[#0047FF]/5 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
              <Link to="/" className="hover:text-[#0047FF]">Inicio</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900">Reparación en {cityName}</span>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="city-h1">
                {cityData?.h1_title || `Reparación de Electrónica en ${cityName}`}
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                {cityData?.intro_text || `Servicio técnico especializado para ${cityName}. Reparamos consolas, móviles, robots aspiradores y más. Envío gratuito ida y vuelta.`}
              </p>

              {/* Search Box */}
              <form onSubmit={handleSearch} className="flex gap-2 max-w-xl">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Buscar tu dispositivo: PS5, iPhone, Conga..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-12"
                    data-testid="seo-search-input"
                  />
                </div>
                <Button type="submit" className="bg-[#0047FF] hover:bg-[#003BCC] h-12 px-6">
                  Buscar
                </Button>
              </form>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-4 bg-white rounded-lg shadow-lg border p-4 max-w-xl">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Resultados:</h3>
                  <div className="space-y-2">
                    {searchResults.map((result, index) => (
                      <Link
                        key={index}
                        to={`/reparacion/${result.data.brand_name?.toLowerCase().replace(' ', '-')}/${result.data.slug}`}
                        className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <Wrench className="w-5 h-5 text-[#0047FF]" />
                        <div>
                          <p className="font-medium">{result.data.name}</p>
                          <p className="text-sm text-gray-500">{result.data.repair_cost_range}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
              {[
                { icon: Truck, text: 'Envío gratis ida y vuelta' },
                { icon: Shield, text: '90 días de garantía' },
                { icon: Clock, text: '7-30 días' },
                { icon: Star, text: '4.8/5 valoración' }
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                  <badge.icon className="w-5 h-5 text-[#0047FF]" />
                  <span>{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brands Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-center">
              Marcas que Reparamos en {cityName}
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {brands.map((brand, i) => (
                <Link
                  key={i}
                  to={`/reparacion/${brand.slug}`}
                  className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#0047FF] hover:shadow-lg transition-all text-center group"
                  data-testid={`brand-card-${brand.slug}`}
                >
                  <h3 className="font-semibold group-hover:text-[#0047FF] transition-colors">
                    {brand.name}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">Ver modelos</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Popular Repairs */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-8">
              Reparaciones más solicitadas en {cityName}
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularRepairs.slice(0, 6).map((repair, i) => (
                <Link
                  key={i}
                  to={`/reparacion/${repair.brand_name?.toLowerCase().replace(' ', '-')}/${repair.slug}`}
                  className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all"
                  data-testid={`repair-card-${repair.slug}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0047FF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Wrench className="w-6 h-6 text-[#0047FF]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">{repair.name}</h3>
                      <p className="text-sm text-gray-500 mb-2">{repair.brand_name}</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-[#0047FF] font-medium">{repair.repair_cost_range}</span>
                        <span className="text-gray-400">|</span>
                        <span className="text-gray-500">{repair.repair_time}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Common problems */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {repair.common_problems?.slice(0, 3).map((problem, j) => (
                      <span key={j} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {problem}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-[#0A0A0A] text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              ¿Necesitas reparar tu dispositivo en {cityName}?
            </h2>
            <p className="text-gray-400 mb-8">
              Envío gratuito desde cualquier punto de {cityData?.province || cityName}. 
              Recibimos tu dispositivo, lo reparamos y te lo devolvemos a casa.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-[#E5FF00] hover:bg-[#d4ee00] text-black">
                  Solicitar Reparación
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="https://wa.me/34624473293">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-gray-200">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#0047FF]" />
              <span className="text-sm text-gray-600">
                Servicio técnico en {cityName} y toda España
              </span>
            </div>
            <div className="flex items-center gap-6">
              <a href="tel:+34624473293" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#0047FF]">
                <Phone className="w-4 h-4" /> 624 47 32 93
              </a>
              <a href="tel:+34642676910" className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700">
                <Phone className="w-4 h-4" /> Emergencias: 642 67 69 10
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SEOLandingPage;
